import {
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
} from '@angular/core';

import { TW_POPOVER_CONTEXT } from './tw-popover-context';

/**
 * `[twPopoverTrigger]` — attribute directive applied to a real
 * `<button>` (preferred — native invoker semantics) or, in
 * hover-only mode, any element that should expose a popover on
 * hover/focus (TWS-122 Wave 2, relaxed under TWS-108 Wave 8 so
 * sidebar `<a routerLink>` rail items can host triggers).
 *
 * ### Why an attribute directive?
 *
 * The native `[popover]` invoker semantics (`popovertarget`) are
 * **button-specific** per the HTML spec — only `<button>` and
 * `<input type="button|submit|reset|image">` have a
 * `popoverTargetElement` IDL property and participate in the
 * browser's open/close routing. On any other element (including
 * Angular custom elements like the previous `<tw-popover-trigger>`
 * host) `popovertarget` is a stray attribute the browser never
 * inspects, and the popover never opens.
 *
 * Hosting the directive on a real `<button>` restores:
 *   - Native click / Enter / Space toggling.
 *   - Light-dismiss and top-layer semantics.
 *   - Escape to close without any JS handlers.
 *
 * In `hover` mode the trigger host can be any element — the
 * directive only wires its own mouse/focus listeners; the
 * `popovertarget` attribute is intentionally NOT applied. This
 * lets sidebar `<a routerLink>` rail items host hover-only
 * triggers without sacrificing navigation semantics.
 *
 * ### Dual-mode behaviour
 *
 * Controlled by the parent's `triggerOn` input:
 *
 *   - **click** (default): applies `popovertarget="<id>"` so the
 *     native `[popover]` API handles open/close via real clicks,
 *     Enter, Space — no JS click handler needed. `aria-haspopup`
 *     is still set so screen readers announce the dialog affordance.
 *
 *   - **hover**: drops `popovertarget` entirely and wires
 *     `mouseenter` / `mouseleave` + `focusin` / `focusout` to the
 *     context's `setOpen(…)`. Focus parity is important — a
 *     hover-only trigger that ignores keyboard focus fails WCAG
 *     2.1.1 (keyboard access) for anyone not using a mouse.
 *
 * Delays for hover mode:
 *   - Show: 150 ms (shorter than tooltip's 300 ms because popovers
 *     are a more deliberate affordance; users who hover a popover
 *     trigger generally intend to open it).
 *   - Hide: 180 ms (long enough to cross the
 *     `var(--twist-spacing-2)` gap between trigger and panel; the
 *     content slot routes its own `mouseenter` back through the
 *     context, so once the cursor lands on the panel the timer is
 *     cancelled and the popover stays open. See `onContentHover`
 *     below — this is the hover-bridge fix for TWS-108 Wave 8).
 *
 * ### `enabled` input
 *
 * When `false`, hover/focus handlers no-op and any pending show
 * timer is cleared. If the trigger is `enabled = false` while
 * the popover is open, the popover is closed. This lets the
 * sidebar gate rail-mode flyout opening on `isRail() ||
 * isCollapsed(group)` without conditional template duplication.
 */
@Directive({
  selector: '[twPopoverTrigger]',
  standalone: true,
  host: {
    'class': 'tw-popover__trigger',
    '[attr.popovertarget]': 'isClickAndEnabled() ? ctx.popoverId : null',
    '[attr.aria-haspopup]': '"dialog"',
    '[attr.aria-expanded]': 'ariaExpanded()',
    '[attr.aria-controls]': 'ctx.popoverId',
    '(mouseenter)': 'onHoverEnter()',
    '(mouseleave)': 'onHoverLeave()',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut()',
  },
})
export class TwPopoverTriggerDirective implements OnInit {
  protected readonly ctx = inject(TW_POPOVER_CONTEXT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Master gate for the trigger. When `false` every handler
   * no-ops, `popovertarget` is stripped, and any open popover is
   * closed. The default is `true` so existing consumers keep
   * working without explicit binding.
   */
  readonly enabled = input<boolean>(true);

  /** Show/hide timer handles — cleared on destroy + on state flip. */
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  /** Observed state for show/hide recheck before timer fires. */
  private hovered = false;
  private focused = false;
  /**
   * True while the cursor is over the popover content panel. The
   * content slot wires its own `mouseenter` / `mouseleave` and
   * routes them through `context.notifyContentHovered(...)` — see
   * the directive class docstring and `onContentHover` below.
   * Treated as equivalent to `hovered` for show/hide decisions, so
   * trigger + content read as a single hover region (TWS-108 Wave 8).
   */
  private contentHovered = false;

  // Hover-mode delays — show is tuned slightly shorter than the
  // tooltip directive's 300 ms since popovers are a more deliberate
  // affordance (users hovering a popover trigger generally want the
  // panel to appear). Hide is 180 ms — long enough for the cursor
  // to traverse the `var(--twist-spacing-2)` gap from trigger to
  // panel without the popover dying in the dead zone. Once the
  // cursor lands on the panel, `onContentHover(true)` cancels the
  // hide timer.
  private static readonly HOVER_SHOW_DELAY_MS = 150;
  private static readonly HOVER_HIDE_DELAY_MS = 180;

  /**
   * Combined gate for `popovertarget` attribute — only emitted
   * when the trigger is enabled AND the parent is in `click`
   * mode. Stripped in hover mode (so the native invoker doesn't
   * fight the JS open path) and when the trigger is disabled
   * (so click is inert).
   */
  protected isClickAndEnabled(): boolean {
    return this.enabled() && this.ctx.triggerOn() === 'click';
  }

  /**
   * Gated `aria-expanded` value (TWS-108 follow-up to 2d26faa8).
   *
   * APG reserves `aria-expanded` for disclosure-button patterns —
   * a control that, when activated, reveals or hides associated
   * content via an explicit user action (click / Enter / Space).
   * Hover popovers (tooltips, hovercards, sidebar flyouts) are
   * NOT disclosure widgets — they auto-open on pointer transit
   * and have no "user-activated state to expose" in the APG
   * sense. Emitting `aria-expanded` on a hover host also leaks
   * the directive's open-state into host CSS rules that legibly
   * target `[aria-expanded]` (the sidebar chevron bug fixed at
   * 2d26faa8 was exactly this — the chevron's rotation matched
   * the popover's hover state instead of the group's actual
   * collapse state).
   *
   * Strategy:
   *   - Disabled trigger → null (attribute omitted).
   *   - Hover trigger → null (attribute omitted).
   *   - Click trigger → "true" | "false" reflecting `ctx.open()`.
   *
   * Returning `null` makes Angular omit the attribute entirely,
   * which is what assistive tech expects (no attribute is a
   * stronger signal than `aria-expanded="false"` on an element
   * that has no disclosure semantics in the first place).
   */
  protected ariaExpanded(): string | null {
    if (!this.enabled()) return null;
    if (this.ctx.triggerOn() !== 'click') return null;
    return this.ctx.open() ? 'true' : 'false';
  }

  /**
   * Set `type="button"` on real <button> hosts so they do not
   * accidentally submit an enclosing form. Anchor and other
   * non-button hosts skip this — `type` is meaningless on them
   * and Angular won't bind it to non-input elements anyway.
   */
  private syncButtonType(): void {
    const el = this.host.nativeElement;
    if (el.tagName === 'BUTTON' && !el.hasAttribute('type')) {
      el.setAttribute('type', 'button');
    }
  }

  ngOnInit(): void {
    const el = this.host.nativeElement;
    this.ctx.registerTriggerElement(el);
    // CSS `anchor-name` takes a dashed-ident directly — NOT a
    // `var()` wrapper. Same pattern as the dropdown trigger.
    el.style.setProperty('anchor-name', this.ctx.anchorName);
    this.syncButtonType();

    // Register the hover-bridge handler — the content slot routes
    // its own `mouseenter` / `mouseleave` through this so trigger
    // + content read as one hover region (TWS-108 Wave 8).
    this.ctx.registerContentHoverHandler((hovered) => this.onContentHover(hovered));

    this.destroyRef.onDestroy(() => {
      this.clearShow();
      this.clearHide();
      this.ctx.registerContentHoverHandler(null);
    });
  }

  constructor() {
    // When the trigger is toggled from enabled → disabled, close
    // any open popover and cancel pending show timers. This is
    // the structural fix for the sidebar's "rail mode dropped but
    // flyout still open" edge — disabling the trigger now closes
    // the popover, instead of relying on caller-side timers.
    effect(() => {
      if (!this.enabled()) {
        this.clearShow();
        this.clearHide();
        if (this.ctx.open()) {
          this.ctx.setOpen(false);
        }
      }
    });
  }

  // ---------------------------------------------------------------
  // Hover / focus handlers — only meaningful in `hover` mode, but
  // safe to attach unconditionally. In `click` mode the context's
  // `triggerOn` signal gates the side effects inside each handler
  // so stray pointer events don't toggle state.
  // ---------------------------------------------------------------

  protected onHoverEnter(): void {
    this.hovered = true;
    if (!this.enabled()) return;
    if (this.ctx.triggerOn() !== 'hover') return;
    this.clearHide();
    this.scheduleShow();
  }

  protected onHoverLeave(): void {
    this.hovered = false;
    if (!this.enabled()) return;
    if (this.ctx.triggerOn() !== 'hover') return;
    this.clearShow();
    this.scheduleHide();
  }

  protected onFocusIn(): void {
    this.focused = true;
    if (!this.enabled()) return;
    if (this.ctx.triggerOn() !== 'hover') return;
    this.clearHide();
    this.scheduleShow();
  }

  protected onFocusOut(): void {
    this.focused = false;
    if (!this.enabled()) return;
    if (this.ctx.triggerOn() !== 'hover') return;
    this.clearShow();
    this.scheduleHide();
  }

  /**
   * Hover-bridge callback wired into the context (TWS-108 Wave 8).
   * The content slot calls this on its own `mouseenter` /
   * `mouseleave`, so cursor traversal across the trigger→panel
   * gap (and any time spent over the panel itself) is treated as
   * continued hover. `mouseleave` schedules a hide on the same
   * grace timer used by the trigger so users can move back and
   * forth between trigger and panel freely.
   *
   * Guarded by `enabled` and `triggerOn === 'hover'` to match the
   * trigger's own handlers — a click-mode popover never sees these
   * calls because the content slot only attaches the listeners in
   * hover mode (see `tw-popover-content.component.ts`).
   */
  onContentHover(hovered: boolean): void {
    this.contentHovered = hovered;
    if (!this.enabled()) return;
    if (this.ctx.triggerOn() !== 'hover') return;
    if (hovered) {
      // Cursor landed on the panel — cancel any pending close and
      // make sure the popover is open (it should already be, but
      // a stray race can leave it closed mid-animation).
      this.clearHide();
      if (!this.ctx.open()) {
        this.ctx.setOpen(true);
      }
    } else {
      // Cursor left the panel — let the same grace timer decide
      // whether to close (so re-entering the trigger keeps it
      // open without flicker).
      this.scheduleHide();
    }
  }

  private scheduleShow(): void {
    this.clearShow();
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      // Recheck: the pointer/focus may have left during the delay.
      if (this.hovered || this.focused || this.contentHovered) {
        this.ctx.setOpen(true);
      }
    }, TwPopoverTriggerDirective.HOVER_SHOW_DELAY_MS);
  }

  private scheduleHide(): void {
    this.clearHide();
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (!this.hovered && !this.focused && !this.contentHovered) {
        this.ctx.setOpen(false);
      }
    }, TwPopoverTriggerDirective.HOVER_HIDE_DELAY_MS);
  }

  private clearShow(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHide(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}

/**
 * Back-compat alias. The component originally shipped as
 * `TwPopoverTriggerComponent` (element selector `<tw-popover-trigger>`),
 * but that selector was silently dropping `popovertarget` on the
 * custom-element host — see the directive class docstring for the
 * spec rationale. Consumers migrating from the old name keep a
 * working import; the symbol is the same class.
 *
 * @deprecated Use `TwPopoverTriggerDirective` and host on a real
 * `<button>` — `<button twPopoverTrigger>…</button>`.
 */
export const TwPopoverTriggerComponent = TwPopoverTriggerDirective;
